"use client";

import { CloseButton } from "@commerce-frontend/ui/base/CloseButton/CloseButton";
import { Icon } from "@commerce-frontend/ui/base/Icon/Icon";
import { Link } from "@commerce-frontend/ui/base/Link/Link";
import { Carousel } from "@commerce-frontend/ui/components/Carousel/Carousel";
import Stars from "@commerce-frontend/ui/components/Stars/Stars";
import { TaxedPrice } from "@commerce-frontend/ui/components/taxed-price/taxed-price";
import { cn } from "@commerce-frontend/ui/utils/classNames";
import type { Locale } from "@commerce-shared/site-config";
import { countryOf, languageOf } from "@commerce-shared/utility";
import { sendGTMEvent } from "@next/third-parties/google";
import type { FragmentOf } from "gql.tada";
import { useTranslations } from "next-intl";
import Image from "next/image";
import React from "react";
import type { CardProductFragment } from "~/components/product/ProductCard/ProductCard.fragments";
import { useVariantUrl } from "~/hooks/product-url";
import { useStaticUrl } from "~/hooks/static-url";
import { generateItemDataFromProduct } from "~/lib/gtm";
import type {
	CategoryPageResultFragment,
	ContentPageResultFragment,
} from "~/lib/queries/graphql/fragments";
import type { SearchResultObject } from "../SearchBarGroup/SearchBarGroup";

type QuickSearchMobileProps = {
	locale: Locale;
	className?: string;
	value?: string;
	recentSearchTerms?: string[];
	searchResult?: SearchResultObject;
	onSelectLink: (
		value: string,
		shouldAddToRecentSearchTerms: boolean,
		shouldCloseSearch?: boolean,
	) => void;
	onSelectProductLink: () => void;
	onClickDeleteLink: (value: string) => void;
};

export const QuickSearchMobile = ({
	locale,
	className,
	value,
	recentSearchTerms,
	searchResult,
	onSelectLink,
	onSelectProductLink,
	onClickDeleteLink,
}: QuickSearchMobileProps) => {
	const t = useTranslations("QuickSearch");
	const availabilityT = useTranslations("Availability");

	const badges = searchResult?.badges;
	const searchTerms = searchResult?.searchTerms;
	const categories = searchResult?.categories;
	const products = searchResult?.products;
	const pages = searchResult?.pages;

	const hasBadges = badges && badges.length > 0;
	const hasRecentSearchTerms =
		recentSearchTerms && recentSearchTerms.length > 0;
	const hasSearchTerms = searchTerms && searchTerms.length > 0;
	const hasCategories = categories && categories.length > 0;
	const hasProducts = products && products.length > 0;
	const hasPages = pages && pages.length > 0;

	const showNoResults =
		!hasBadges &&
		!hasSearchTerms &&
		!hasCategories &&
		!hasProducts &&
		!hasPages;

	return (
		<div className={className}>
			{hasBadges && (
				<>
					<div className="py-8" aria-label={t("accessibilityBadges")}>
						<div className="text-base font-bold text-main">
							<div className="mb-4 px-4">{t("popularSearchTerms")}</div>
							<div className="flex flex-row flex-wrap items-center justify-start gap-2">
								<Carousel
									innerClassName="gap-2 max-md:px-0" // mb-4
									className="w-full"
									carouselClassName="hidden"
								>
									{badges.map((badge, index) => (
										<QuickSearchMobileBadge
											key={index}
											badge={badge.value}
											id={badge.id}
											index={index}
											onClick={() => onSelectLink(badge.value, true)}
											algoliaQueryId={searchResult.algoliaQueryId}
										/>
									))}
								</Carousel>
							</div>
						</div>
					</div>
					<hr />
				</>
			)}
			{hasRecentSearchTerms && (
				<>
					<div
						className="px-4 py-8"
						aria-label={t("accessibilityRecentSearchTerms")}
					>
						<div className="flex flex-col gap-2 text-base font-bold text-main">
							<div className="mb-2">{t("searchTerms")}</div>
							{recentSearchTerms.map((term, index) => (
								<QuickSearchMobileRecentSearchTerm
									key={index}
									term={term}
									index={index}
									onClick={() => onSelectLink(term, true)}
									onClickDeleteLink={() => onClickDeleteLink(term)}
								/>
							))}
						</div>
					</div>
					<hr />
				</>
			)}
			{(hasCategories || hasSearchTerms) && (
				<>
					<div className="px-4 py-8" aria-label={t("accessibilitySearchTerms")}>
						<div className="flex flex-col gap-2 text-base font-bold text-main">
							{categories?.map((category, index) => (
								<QuickSearchMobileCategory
									key={index}
									category={category}
									locale={locale}
									id={category.name}
									index={index}
									onClick={() => onSelectLink(category.name, true)}
									algoliaQueryId={searchResult.algoliaQueryId}
								/>
							))}
							{searchTerms?.map((searchTerm, index) => (
								<QuickSearchMobileSearchTerm
									key={index}
									searchTerm={searchTerm.value}
									id={searchTerm.id}
									index={index}
									onClick={() => onSelectLink(searchTerm.value, true)}
									algoliaQueryId={searchResult.algoliaQueryId}
								/>
							))}
						</div>
					</div>
					<hr />
				</>
			)}
			{hasProducts && (
				<>
					<div aria-label="products">
						{products.map((product, index) => (
							<React.Fragment key={index}>
								<QuickSearchMobileProduct
									key={index}
									locale={locale}
									product={product}
									index={index}
									onClick={() => {
										onSelectProductLink();
										const variant = product.variants[0];
										sendGTMEvent({
											event: "select_item",
											country: countryOf(locale).toLowerCase(),
											language: languageOf(locale).toLowerCase(),
											platform: "commercetools",
											item_list_name: "search_quick",
											items: generateItemDataFromProduct(
												availabilityT,
												product,
												1,
												variant,
												{
													index: index + 1,
													query_id: searchResult.algoliaQueryId,
												},
											),
										});
									}}
									algoliaQueryId={searchResult.algoliaQueryId}
								/>
								<hr />
							</React.Fragment>
						))}
					</div>
				</>
			)}
			{hasPages && (
				<>
					<div className="px-4 py-8" aria-label={t("accessibilityPages")}>
						<div className="flex flex-col gap-2 text-base font-bold text-main">
							<div className="mb-2">{t("pages")}</div>
							{pages.map((page, index) => (
								<QuickSearchMobilePage
									key={index}
									page={page}
									index={index}
									onClick={() => onSelectLink(page.name, true)}
									algoliaQueryId={searchResult.algoliaQueryId}
								/>
							))}
						</div>
					</div>
				</>
			)}
			{showNoResults && value && (
				<QuickSearchMobileNoResult
					value={value}
					onClick={() => onSelectLink(value, false)}
				/>
			)}
			{/*<div aria-label="support-and-advice"></div>*/}
		</div>
	);
};

type QuickSearchMobileBadgeProps = {
	badge: string;
	id: string;
	index: number;
	onClick: () => void;
	algoliaQueryId: string | null | undefined;
};

const QuickSearchMobileBadge = ({
	badge,
	id,
	index,
	onClick,
	algoliaQueryId,
}: QuickSearchMobileBadgeProps) => {
	const searchUrl = useStaticUrl("search");
	return (
		<Link
			aria-label={"badge_" + badge}
			data-insights-object-id={id}
			data-insights-position={index + 1}
			data-insights-query-id={algoliaQueryId}
			key={index}
			href={createSearchUrl(badge, searchUrl)}
			onClick={onClick}
			className="rounded border bg-background p-1 text-center text-sm font-bold text-blue-300 hover:bg-gray-100"
		>
			{badge}
		</Link>
	);
};

type QuickSearchMobileRecentSearchTermProps = {
	term: string;
	index: number;
	onClick: () => void;
	onClickDeleteLink: () => void;
};

const QuickSearchMobileRecentSearchTerm = ({
	term,
	index,
	onClick,
	onClickDeleteLink,
}: QuickSearchMobileRecentSearchTermProps) => {
	const searchUrl = useStaticUrl("search");
	return (
		<div className="flex justify-between" key={index}>
			<div className="flex items-center">
				<Icon icon="clock" className="mr-2" size="sm" />
				<Link
					aria-label={"recentsearchterm_" + term}
					href={createSearchUrl(term, searchUrl)}
					onClick={onClick}
					className="font-normal text-blue-300 hover:text-main hover:underline"
				>
					{term}
				</Link>
			</div>
			<CloseButton
				variant="info"
				classes={{ icon: "ml-2" }}
				iconSize="sm"
				onClick={onClickDeleteLink}
				tabIndex={-1}
			/>
		</div>
	);
};

type QuickSearchMobileCategoryProps = {
	id: string;
	category: FragmentOf<typeof CategoryPageResultFragment>;
	locale: Locale;
	index: number;
	onClick: () => void;
	algoliaQueryId: string | null | undefined;
};

const QuickSearchMobileCategory = ({
	id,
	category,
	index,
	onClick,
	algoliaQueryId,
}: QuickSearchMobileCategoryProps) => (
	<div
		aria-label={"category_" + category.name}
		className="flex justify-between"
		key={index}
		data-insights-object-id={id}
		data-insights-position={index + 1}
		data-insights-query-id={algoliaQueryId}
	>
		<div className="flex items-center">
			<Link href={category.url} onClick={onClick}>
				<span className="font-normal text-blue-300 hover:text-main hover:underline">
					{category.name}
				</span>
				{category.parent && (
					<span className="ml-2 text-sm font-normal text-muted">
						in {category.parent}
					</span>
				)}
			</Link>
		</div>
	</div>
);

type QuickSearchMobileSearchTermProps = {
	id: string;
	searchTerm: string;
	index: number;
	onClick: () => void;
	algoliaQueryId: string | null | undefined;
};

const QuickSearchMobileSearchTerm = ({
	id,
	searchTerm,
	index,
	onClick,
	algoliaQueryId,
}: QuickSearchMobileSearchTermProps) => {
	const searchUrl = useStaticUrl("search");
	return (
		<div
			aria-label={"searchterm_" + searchTerm}
			className="flex justify-between"
			key={index}
			data-insights-object-id={id}
			data-insights-position={index + 1}
			data-insights-query-id={algoliaQueryId}
		>
			<div className="flex items-center">
				<Link href={createSearchUrl(searchTerm, searchUrl)} onClick={onClick}>
					<span className="font-normal text-blue-300 hover:text-main hover:underline">
						{searchTerm}
					</span>
				</Link>
			</div>
		</div>
	);
};

type QuickSearchMobileProductProps = {
	locale: Locale;
	product: FragmentOf<typeof CardProductFragment>;
	index: number;
	onClick: () => void;
	algoliaQueryId: string | null | undefined;
};

const QuickSearchMobileProduct = ({
	locale,
	product,
	index,
	onClick,
	algoliaQueryId,
}: QuickSearchMobileProductProps) => {
	const t = useTranslations("QuickSearch");
	const productPriceT = useTranslations("ProductPrice");

	const variant = product.variants[0];
	const variantUrl = useVariantUrl(product, variant);
	const hasReviews = product.reviews?.averageRating && product.reviews?.count;

	const isMultivariantProduct = product.numberOfVariants > 1;

	return (
		<div
			className="flex flex-row gap-4 p-4"
			key={index}
			title={"product_" + product.name}
			aria-label={t("accessibilityProducts")}
			data-insights-object-id={variant.sku}
			data-insights-position={index + 1}
			data-insights-query-id={algoliaQueryId}
		>
			{product.image?.url ? (
				<Image
					src={product.image.url}
					alt={product.image.alt ?? product.name}
					width={64}
					height={40}
					sizes="64px"
				/>
			) : (
				<span className="aspect-[3/2] h-10 w-16 bg-gray-100" />
			)}
			<div className="flex flex-col justify-center gap-1 truncate">
				<Link
					href={variantUrl}
					onClick={onClick}
					className="overflow-hidden text-ellipsis text-sm font-bold text-main"
				>
					{product?.name}
				</Link>
				<div className="flex flex-row items-center justify-start gap-2 text-xs text-muted">
					{hasReviews && (
						<Stars
							inlineMode={true}
							link={false}
							rating={product.reviews?.averageRating}
							classes={{
								text: "flex gap-2 justify-center items-center",
								parent: "items-start",
								stars: "-mr-0.5",
							}}
						>
							<span className="text-xs text-muted">
								({product.reviews?.count})
							</span>
						</Stars>
					)}
					{hasReviews ? (
						<span className="border-l pl-2">
							{t("articleNumber")} {variant?.sku}
						</span>
					) : (
						<>
							{t("articleNumber")} {variant?.sku}
						</>
					)}
				</div>
				<div>
					<div className="flex items-center gap-2 sm:grid-in-price">
						{isMultivariantProduct ? (
							<span className="text-xs font-normal text-muted">
								{productPriceT("from")}{" "}
							</span>
						) : (
							variant.priceBefore && (
								<TaxedPrice
									price={variant.priceBefore}
									locale={locale}
									className="strike relative text-xs text-muted"
									showCurrencySign={false}
								/>
							)
						)}
						<TaxedPrice
							price={variant.price}
							locale={locale}
							className={cn(
								"text-base font-bold leading-none",
								variant.priceBefore ? "text-accent" : "text-main",
							)}
							showCurrencySign={false}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

type QuickSearchMobilePageProps = {
	page: FragmentOf<typeof ContentPageResultFragment>;
	index: number;
	onClick: () => void;
	algoliaQueryId: string | null | undefined;
};

const QuickSearchMobilePage = ({
	page,
	index,
	onClick,
	algoliaQueryId,
}: QuickSearchMobilePageProps) => (
	<div
		aria-label={"page_" + page.name}
		className="flex justify-between"
		key={index}
		data-insights-object-id={page.name}
		data-insights-position={index + 1}
		data-insights-query-id={algoliaQueryId}
	>
		<div className="flex items-center">
			<Link href={page.url} onClick={onClick}>
				<span className="font-normal text-blue-300 hover:text-main hover:underline">
					{page.name}
				</span>
			</Link>
		</div>
	</div>
);

type QuickSearchMobileNoResultProps = {
	value: string;
	onClick: () => void;
};

const QuickSearchMobileNoResult = ({
	value,
	onClick,
}: QuickSearchMobileNoResultProps) => {
	const t = useTranslations("QuickSearch");
	const searchUrl = useStaticUrl("search");
	return (
		<>
			<div className="px-4 py-8">
				<Link
					href={createSearchUrl(value, searchUrl)}
					onClick={onClick}
					className="flex items-center gap-2 text-base text-blue-300 hover:text-main hover:underline"
				>
					<Icon icon={"chevron-right"} className="bg-blue-300" />
					{t("noResults", {
						value: value,
					})}
				</Link>
			</div>
			<hr />
		</>
	);
};

const createSearchUrl = (value: string, searchUrl: string) => {
	const searchParams = new URLSearchParams();
	searchParams.set("q", value);
	return `${searchUrl}?${searchParams.toString()}`;
};
